// management requests are sent to the management api

import { api } from 'api';
import axios, { type AxiosError, type AxiosInstance } from 'axios';
import { type Role } from 'components/Permission/permission.types';
import { store } from 'store';
import { showNotification } from 'store/notification.slice';
import { CLIENT_THEME } from 'utils/clientTheme';
import { hasAccessToMultipleCustomers } from 'utils/validators/role.validation';

export default abstract class BaseAPI {
  protected readonly axiosInstance: AxiosInstance;
  protected readonly API: any;
  protected readonly accessToken: string;
  protected readonly role: Role;
  protected readonly customerId: string;

  protected handleUnauthenticated() {}

  protected handleUnauthorized() {}

  private setupInterceptors() {
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const statusCode = error.response?.status;
        if (statusCode === 401) {
          console.error('User not authenticated');
          this.handleUnauthenticated();
        } else if (statusCode === 403) {
          console.error('User not authorized');
          this.handleUnauthorized();
        } else if (statusCode === 500) {
          store.dispatch(
            showNotification({ type: 'error', message: 'Something went wrong' })
          );
        }
        return await Promise.reject(error);
      }
    );
  }

  constructor() {
    const state = store.getState();
    this.accessToken = state.authReducer.accessToken;
    this.role = state.authReducer.role as Role;
    this.customerId = state.persistedReducer.customer.id;

    // configure axios instance
    this.axiosInstance = axios.create({
      baseURL: `${process.env.REACT_APP_MANAGEMENT_API_URL ?? ''}/api/`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.accessToken}`,
        'x-client-theme': CLIENT_THEME,
      },
      params: {
        // Add the selected customerId as query parameter if the user is an internal user
        ...(hasAccessToMultipleCustomers(this.role) && {
          customerId: this.customerId,
        }),
      },
    });

    this.setupInterceptors();
    this.API = api(this.axiosInstance);
  }
}
